import React from 'react';
import { FreeVisual } from './FreeVisual';
import { ObjectVisualContent } from './ObjectVisualContent';
import { makeZoneDropHandlers } from './zoneDropHandlers';

/**
 * Renders the "Report Header (once)" and "Report Footer (once)" violet drop zones
 * that sit above and below the main canvas in report-designer mode.
 *
 * The zone shows a placeholder until at least one visual with `_zone` matching
 * `kind` (`report_header` | `report_footer`) is dropped in. Each visual renders as
 * a FreeVisual containing an ObjectVisualContent.
 */
export function ReportZoneArea({
  kind,
  visuals,
  visualResults,
  activeVisual, setActiveVisual,
  updateVisual,
  setSavedClean,
  handleVisualContextMenu,
  getTransformedData,
  getMaxZIndex, setVisuals, reportHeader, reportFooter,
  allFields, formatValue,
}) {
  const isHeader = kind === 'report_header';
  const label = isHeader ? 'Report Header (once)' : 'Report Footer (once)';
  const placeholder = isHeader
    ? 'Appears once at the top — report title, date range, summary KPIs'
    : 'Appears once at the end — grand totals, signatures, notes';
  const variant = isHeader ? 'report-header' : 'report-footer';
  const wrapperClass = isHeader ? 'mb-3' : 'mt-3';
  const testId = isHeader ? 'report-header-zone-once' : 'report-footer-zone-once';

  const drop = makeZoneDropHandlers({
    zone: kind,
    getMaxZIndex, setVisuals, setActiveVisual,
    reportHeader, reportFooter,
  });

  const zoneVisuals = visuals.filter(v => v._zone === kind);

  return (
    <div
      className={`${wrapperClass} border-2 border-dashed border-violet-400/30 rounded-xl relative bg-violet-500/5`}
      style={{ minHeight: 50 }}
      data-testid={testId}
      onDragOver={drop.onDragOver}
      onDragLeave={drop.onDragLeave}
      onDrop={drop.onDrop}
    >
      <span className="absolute top-1 left-2 text-[8px] text-violet-400/60 font-semibold uppercase">{label}</span>
      {zoneVisuals.length === 0 && (
        <div className="flex items-center justify-center h-full text-[10px] text-violet-400/40 py-4">{placeholder}</div>
      )}
      {zoneVisuals.map(v => {
        const fp = v.freePosition || { x: 10, y: 10, w: 200, h: 30 };
        const vResult = (visualResults[v.id] || {}).result;
        const vData = getTransformedData(v.id) || vResult?.data || [];
        return (
          <FreeVisual
            key={v.id}
            position={fp}
            zIndex={v.zIndex || 10}
            isActive={activeVisual === v.id}
            onSelect={() => setActiveVisual(v.id)}
            onPositionChange={(pos) => { updateVisual(v.id, { freePosition: pos }); setSavedClean(false); }}
            onContextMenu={(e) => handleVisualContextMenu(e, v.id)}
          >
            <ObjectVisualContent
              v={v} onUpdate={(patch) => updateVisual(v.id, patch)} variant={variant}
              vData={vData} allFields={allFields} formatValue={formatValue}
            />
          </FreeVisual>
        );
      })}
    </div>
  );
}
